import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import {
  Menu, X, Globe, ChevronDown, CreditCard, Briefcase, ShieldCheck,
  Coins, PiggyBank, Landmark,
} from "lucide-react";

const SERVICE_LINKS = [
  { slug: "online-bank", icon: CreditCard, en: "Online Crypto Banking", fr: "Banque en ligne crypto" },
  { slug: "asset-management", icon: Briefcase, en: "Asset Management", fr: "Gestion d'actifs" },
  { slug: "insurance", icon: ShieldCheck, en: "Insurance", fr: "Assurance" },
  { slug: "crypto-loans", icon: Coins, en: "Crypto Loans", fr: "Prêts crypto" },
  { slug: "staking", icon: PiggyBank, en: "Staking & Yield", fr: "Staking & Rendement" },
  { slug: "private-bank", icon: Landmark, en: "Private Banking", fr: "Banque privée" },
];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "/#solutions", label: t("nav.solutions") },
    { href: "/#markets", label: t("nav.markets") },
    { href: "/#news", label: t("nav.news") },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#E6EBF1] shadow-[0_1px_12px_rgba(10,37,64,0.06)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/"><Logo light /></Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-[#425466] hover:text-[#0A2540] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-services-dropdown"
              className="flex items-center gap-1 text-sm font-medium text-[#425466] hover:text-[#0A2540] transition-colors outline-none"
            >
              {t("nav.services")} <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border border-[#E6EBF1] text-[#0A2540] shadow-xl rounded-xl p-2 w-64">
              {SERVICE_LINKS.map((s) => (
                <DropdownMenuItem key={s.slug} asChild className="focus:bg-[#F6F9FC] cursor-pointer rounded-lg">
                  <Link to={`/services/${s.slug}`} className="flex items-center gap-3 px-2 py-2.5">
                    <s.icon size={16} className="text-[#635BFF]" />
                    <span className="text-sm font-medium">{lang === "fr" ? s.fr : s.en}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/contact" className="text-sm font-medium text-[#425466] hover:text-[#0A2540] transition-colors">
            {t("nav.contact")}
          </Link>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "fr" : "en")}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#425466] hover:text-[#0A2540] border border-[#E6EBF1] bg-white rounded-full px-3 py-1.5 transition-colors"
          >
            <Globe size={13} />
            {lang === "en" ? "FR" : "EN"}
          </button>
          {user && user.role === "admin" ? (
            <Link to="/ucp-admin">
              <Button size="sm" className="bg-[#635BFF] hover:bg-[#0A2540] text-white rounded-full px-5" data-testid="nav-admin-btn">
                Admin
              </Button>
            </Link>
          ) : user ? (
            <Link to="/dashboard">
              <Button size="sm" className="bg-[#635BFF] hover:bg-[#0A2540] text-white rounded-full px-5" data-testid="nav-dashboard-btn">
                {t("nav.dashboard")}
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button size="sm" variant="ghost" className="text-[#0A2540] hover:bg-[#F6F9FC] rounded-full px-4" data-testid="nav-login-btn">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-[#635BFF] hover:bg-[#0A2540] text-white rounded-full px-5 shadow-lg shadow-[#635BFF]/25" data-testid="nav-register-btn">
                  {t("nav.openAccount")}
                </Button>
              </Link>
            </>
          )}
        </div>
        <button
          className="lg:hidden text-[#0A2540]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E6EBF1] px-5 py-4 space-y-3 shadow-xl max-h-[75vh] overflow-y-auto">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)}
               className="block text-sm font-medium text-[#425466]">{l.label}</a>
          ))}
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 pt-2">{t("nav.services")}</p>
          {SERVICE_LINKS.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 text-sm font-medium text-[#425466]">
              <s.icon size={15} className="text-[#635BFF]" /> {lang === "fr" ? s.fr : s.en}
            </Link>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} className="block text-sm font-medium text-[#425466] pt-1">
            {t("nav.contact")}
          </Link>
          <div className="flex items-center gap-3 pt-2">
            <button
              data-testid="lang-toggle-mobile"
              onClick={() => setLang(lang === "en" ? "fr" : "en")}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#425466] border border-[#E6EBF1] rounded-full px-3 py-1.5"
            >
              <Globe size={13} /> {lang === "en" ? "FR" : "EN"}
            </button>
            <Link to="/login" className="text-sm font-medium text-[#0A2540]">{t("nav.login")}</Link>
            <Link to="/register" className="text-sm text-[#635BFF] font-semibold">{t("nav.openAccount")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
